// src/components/MAP/utils/mapUtils.js
import L from "leaflet";
import { RouteIconsManager } from "../routeIconsManager";

/**
 * 计算路线点集合的边界，并添加缓冲区
 * @param {Array} allRoutePoints - 所有路线点的数组
 * @param {number} bufferPercentage - 缓冲区百分比，默认为0.02(2%)
 * @returns {L.LatLngBounds|null} - 计算后的边界，如果点为空则返回null
 */
export const calculateBoundsWithBuffer = (allRoutePoints, bufferPercentage = 0.02) => {
  if (!allRoutePoints.length) return null;

  const bounds = L.latLngBounds(allRoutePoints[0], allRoutePoints[0]);
  allRoutePoints.forEach((point) => {
    bounds.extend(point);
  });

  const southWest = bounds.getSouthWest();
  const northEast = bounds.getNorthEast();
  const latDiff = Math.abs(northEast.lat - southWest.lat);
  const lngDiff = Math.abs(northEast.lng - southWest.lng);
  const latBuffer = latDiff * bufferPercentage;
  const lngBuffer = lngDiff * bufferPercentage;

  const bufferedBounds = L.latLngBounds(
    [southWest.lat - latBuffer, southWest.lng - lngBuffer],
    [northEast.lat + latBuffer, northEast.lng + lngBuffer]
  );

  return bufferedBounds;
};

// 在 mapUtils.js 中添加
/**
 * 处理CSV数据并提取路线坐标和时间偏移
 * @param {string} data - CSV文本数据
 * @returns {Object} 包含routeLatLngs和timeOffsets的对象
 */
export const processCSVData = (data) => {
    const lines = data.split(/\r\n|\n/);
    const routeLatLngs = [];
    const timeOffsets = [];
    let startTime = null;
  
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].split(",");
      if (line.length >= 4) {
        const time = parseFloat(line[0].trim());
        const lat = parseFloat(line[2].trim());
        const lng = parseFloat(line[3].trim());
        if (!startTime) startTime = time;
        const adjustedTime = (time - startTime) / 1000;
        if (!isNaN(lat) && !isNaN(lng)) {
          routeLatLngs.push([lat, lng]);
          timeOffsets.push(adjustedTime);
        }
      }
    }
    return { routeLatLngs, timeOffsets };
  };

/**
 * 创建路线可视化
 * @param {L.Map} mapInstance - Leaflet地图实例
 * @param {Array} routeLatLngs - 路线坐标数组
 * @param {number} index - 路线索引
 * @param {Object} options - 配置选项
 * @returns {Object} 路线可视化对象
 */
// 在 mapUtils.js 中修改 createRouteVisualization 函数

export const createRouteVisualization = (
    mapInstance, 
    routeLatLngs, 
    index, 
    options = {}
  ) => {
    if (!mapInstance || !routeLatLngs || routeLatLngs.length === 0) return null;
  
    const FIXED_TRAJECTORY_COLOR = "#034ec9";
    const FIXED_OPACITY = 0.3;
    
    const {
      onTrajectoryClick = null,
      onMarkerClick = null,
      timeframe = "11AM",
      timeOffsets = [],
      initiallyHideMarker = false,    // 新增：初始是否隐藏
    } = options;
  
    // 创建背景轨迹 - 显示完整轨迹
    const backgroundPolyline = L.polyline(routeLatLngs, {
      color: FIXED_TRAJECTORY_COLOR,
      weight: 8,
      opacity: FIXED_OPACITY,
      lineCap: "round",
      lineJoin: "round"
    }).addTo(mapInstance);
  
    // 创建动态轨迹 - 初始为空
    const dynamicPolyline = L.polyline([], {
      color: FIXED_TRAJECTORY_COLOR,
      weight: 2,
      opacity: 1,
      lineCap: "round",
      lineJoin: "round",
    }).addTo(mapInstance);

    // 创建机器人标记
    const robotIcon = L.divIcon({
      html: `<img src="/robot.gif" style="width: 30px; height: 30px;">`,
      className: 'robot-gif-marker',
      iconSize: [1, 1],
      iconAnchor: [15, 15]
    });

    // 创建标记但不立即添加到地图
    const marker = L.marker(routeLatLngs[0], {
      icon: robotIcon
    });

    // 确保z-index高于其他元素
    marker.setZIndexOffset(1000);
  
    // 添加点击事件
    if (onTrajectoryClick) {
      backgroundPolyline.on('click', () => onTrajectoryClick(index));
    }
  
    if (onMarkerClick) {
      marker.on('click', () => onMarkerClick(index));
    }
  
    // 确保样式一致性
    backgroundPolyline.setStyle({
      color: FIXED_TRAJECTORY_COLOR,
      opacity: FIXED_OPACITY,
      weight: 8
    });

    // 添加更新机器人位置的方法
 // 在 mapUtils.js 的 createRouteVisualization 函数中修改 updateRobotPosition 方法
const updateRobotPosition = (time, options = {}) => {
  if (!routeLatLngs || routeLatLngs.length === 0 || !timeOffsets || timeOffsets.length === 0) return;
  
  // 找到最接近当前时间的点
  let currentIndex = 0;
  for (let i = 0; i < timeOffsets.length; i++) {
    if (timeOffsets[i] <= time) {
      currentIndex = i;
    } else {
      break;
    }
  }
  
  // 更新动态轨迹线 - 显示从起点到当前位置的轨迹
  const currentPath = routeLatLngs.slice(0, currentIndex + 1);
  dynamicPolyline.setLatLngs(currentPath);
  
  // 更新机器人位置到轨迹线的末端
  const currentPoint = currentPath[currentPath.length - 1];
  marker.setLatLng(currentPoint);
  
  // 确保机器人标记在地图上可见
  if (time > 0 && !initiallyHideMarker && !mapInstance.hasLayer(marker)) {
    marker.addTo(mapInstance);
  } else if (time === 0 && mapInstance.hasLayer(marker)) {
    // 如果时间为0，移除机器人标记
    marker.remove();
  }
  
  // 确保机器人标记在最上层
  if (marker && typeof marker.bringToFront === 'function') {
    marker.bringToFront();
  }
  
  // 如果没有禁用自动平移，并且机器人不在视图中，将地图中心移动到机器人位置
  const noPan = options && options.noPan;
  if (!noPan && !mapInstance.getBounds().contains(currentPoint)) {
    mapInstance.setView(currentPoint, mapInstance.getZoom(), {
      animate: false, // 禁用动画以减少性能开销
      duration: 0
    });
  }
};
  
    return { dynamicPolyline, backgroundPolyline, marker, updateRobotPosition };
  };